import { useStore } from '@tanstack/react-form'
import z from 'zod'

import {
  APPOINTMENT_TYPES,
  APPOINTMENT_TYPES_BY_ID,
  type AppointmentTypeId,
  appointmentTypeIdSchema,
  type ClinicId,
  clinicIdSchema,
  isAppointmentTypeAvailableAtClinic,
  URGENCY_LEVELS,
  type UrgencyId,
  type WithNullable,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'
import { PatientSearch } from '@/frontend/features/patient/patient-search'
import { useNextAvailableSlot } from '@/frontend/hooks/api/use-next-available-slot'
import { useCopyToClipboard } from '@/frontend/hooks/use-copy-to-clipboard'

import { useCreateAppointmentLink } from './use-create-appointment-link'

const schema = z.object({
  patientId: z.string('Please select a patient'),
  clinicId: clinicIdSchema,
  appointmentTypeId: appointmentTypeIdSchema,
  durationMinutes: z.number().int().positive().nullish(),
  urgencyId: z.enum(['high', 'medium', 'low']).optional(),
  staffId: z.string().nullish(),
  reasonForVisit: z.string().optional(),
})
type AppointmentLinkFormDefaultValues = WithNullable<z.infer<typeof schema>>

interface AvailabilityHintProps {
  clinicId: ClinicId
  appointmentTypeId: AppointmentTypeId
  durationMinutes?: number
  staffId?: string | null
  urgencyId?: UrgencyId
}

/** Displays the next available slot once the required booking fields are filled. */
function AvailabilityHint({
  clinicId,
  appointmentTypeId,
  durationMinutes,
  staffId,
  urgencyId,
}: AvailabilityHintProps) {
  const { data: slots, isLoading } = useNextAvailableSlot({
    clinicId,
    appointmentTypeId,
    staffId,
    urgencyId,
    durationMinutes,
    enabled: true,
  })

  if (isLoading) return <p className="text-muted-foreground">Checking availability&hellip;</p>
  if (!slots || slots.length === 0)
    return <p className="text-muted-foreground">No availability in the next 7 days</p>
  return (
    <p className="text-muted-foreground">
      {`Next available: ${new Date(slots[0]).toLocaleString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/London',
      })}`}
    </p>
  )
}

interface AppointmentLinkFormProps {
  /** Pre-select a patient when opening from a patient context. */
  defaultClinicId?: ClinicId
  defaultAppointmentTypeId?: AppointmentTypeId
  defaultPatientId?: string
  onSuccess?: (url: string) => void
}

type SubmitAction = 'copy' | 'select'

export function AppointmentLinkForm({
  defaultClinicId,
  defaultAppointmentTypeId,
  defaultPatientId,
  onSuccess,
}: AppointmentLinkFormProps) {
  const { mutateAsync: createLink } = useCreateAppointmentLink()
  const { copy } = useCopyToClipboard()

  const form = useAppForm({
    defaultValues: {
      appointmentTypeId: defaultAppointmentTypeId ?? APPOINTMENT_TYPES[0].id,
      urgencyId: 'medium',
      clinicId: defaultClinicId,
      ...(defaultPatientId ? { patientId: defaultPatientId } : {}),
    } as AppointmentLinkFormDefaultValues,
    onSubmitMeta: {} as { action: SubmitAction },
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value, formApi, meta }) => {
      const parsed = schema.parse(value)
      try {
        const url = await createLink({
          patientId: parsed.patientId,
          clinicId: parsed.clinicId,
          appointmentTypeId: parsed.appointmentTypeId,
          // Omitted duration tells the backend to use the appointment type's default.
          ...(parsed.durationMinutes != null && { durationMinutes: parsed.durationMinutes }),
          // Urgency only applies to team-wide (any-vet) bookings; omit it for vet-specific ones.
          ...(parsed.staffId ? { staffId: parsed.staffId } : { urgencyId: parsed.urgencyId }),
          reasonForVisit: parsed.reasonForVisit || undefined,
        })
        if (meta.action === 'copy') {
          await copy(url, 'Link')
        } else {
          window.open(url, '_blank')
        }
        onSuccess?.(url)
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create booking link',
            fields: {},
          },
        })
      }
    },
  })

  // Read field values from the store to drive data fetching and conditional rendering.
  const clinicId = useStore(form.store, (s) => s.values.clinicId) ?? ''
  const staffId = useStore(form.store, (s) => s.values.staffId)
  const patientId = useStore(form.store, (s) => s.values.patientId) ?? ''
  const appointmentTypeId = useStore(form.store, (s) => s.values.appointmentTypeId) ?? ''
  const urgencyId = useStore(form.store, (s) => s.values.urgencyId) ?? undefined
  const durationMinutes = useStore(form.store, (s) => s.values.durationMinutes)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
      }}
    >
      <p className="text-sm font-semibold uppercase text-muted-foreground mb-3">
        Generate a booking link
      </p>
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          {!defaultPatientId && (
            <form.Field name="patientId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Patient"
                  render={({ id, disabled }) => (
                    <PatientSearch
                      id={id}
                      aria-invalid={!!field.state.meta.errors.length}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled}
                    />
                  )}
                />
              )}
            </form.Field>
          )}

          <form.Field
            name="clinicId"
            listeners={{
              onChange: ({ value }) => {
                // If switching clinic invalidates the currently selected appointment type,
                // clear the appointment type
                const currentType = form.getFieldValue('appointmentTypeId')
                if (!currentType || !value) return
                if (isAppointmentTypeAvailableAtClinic(currentType, value)) return
                form.setFieldValue('appointmentTypeId', null)
              },
            }}
          >
            {(field) => (
              <FieldWrapper
                field={field}
                label="Clinic"
                render={({ id, disabled }) => (
                  <ClinicSearchWithDefault
                    id={id}
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </form.Field>

          <form.AppField
            name="appointmentTypeId"
            listeners={{
              onChange: ({ value }) => {
                if (!value) return
                const type = APPOINTMENT_TYPES_BY_ID[value]
                if (type) form.setFieldValue('durationMinutes', null)
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Appointment type"
                items={APPOINTMENT_TYPES.filter((t) =>
                  clinicId ? isAppointmentTypeAvailableAtClinic(t.id, clinicId) : true
                )
                  .map((a) => ({ label: a.name, value: a.id }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
              />
            )}
          </form.AppField>

          <form.AppField name="durationMinutes">
            {(field) => (
              <field.NumericInputField
                label="Appointment duration (minutes)"
                description="Leave blank to use the suggested length for this appointment type."
                placeholder={String(APPOINTMENT_TYPES_BY_ID[appointmentTypeId]?.duration ?? '-')}
                min={1}
              />
            )}
          </form.AppField>

          <form.Field name="staffId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Vet"
                disabled={!clinicId}
                render={({ id, disabled }) => (
                  <StaffSearch
                    id={id}
                    value={field.state.value ?? null}
                    // A cleared selection means a team-wide ("Any vet") booking.
                    onChange={(staff) => field.handleChange(staff?.id ?? undefined)}
                    onBlur={field.handleBlur}
                    placeholder="Any vet"
                    allowClear
                    disabled={disabled}
                  />
                )}
              />
            )}
          </form.Field>

          {/* Urgency only applies when no specific vet is chosen. */}
          {!staffId && (
            <form.Field name="urgencyId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Urgency"
                  render={({ disabled }) => (
                    <div className="flex gap-2">
                      {URGENCY_LEVELS.map((level) => (
                        <Button
                          key={level.id}
                          type="button"
                          variant={field.state.value === level.id ? 'default' : 'outline'}
                          className="flex-1"
                          disabled={disabled}
                          onClick={() => field.handleChange(level.id)}
                        >
                          {level.label}
                        </Button>
                      ))}
                    </div>
                  )}
                />
              )}
            </form.Field>
          )}

          <form.AppField name="reasonForVisit">
            {(field) => (
              <field.TextareaField
                label="Visit reason"
                description="Context for other Snoots staff. Not shared with the member."
                placeholder="e.g. member says they'll have AHC questionnaire completed by 14th Jun"
                rows={3}
              />
            )}
          </form.AppField>

          {/* Show next available slot once required fields are filled. */}
          {!!(patientId && clinicId && appointmentTypeId) && (
            <AvailabilityHint
              clinicId={clinicId}
              appointmentTypeId={appointmentTypeId}
              staffId={staffId}
              urgencyId={urgencyId}
              durationMinutes={
                durationMinutes ?? APPOINTMENT_TYPES_BY_ID[appointmentTypeId].duration
              }
            />
          )}
        </div>

        <div className="pt-3 flex justify-end gap-x-2">
          <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
            {([canSubmit, isSubmitting]) => (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  disabled={!canSubmit || isSubmitting}
                  onClick={() => form.handleSubmit({ action: 'copy' })}
                >
                  {isSubmitting ? 'Generating...' : 'Generate & Copy link'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  disabled={!canSubmit || isSubmitting}
                  onClick={() => form.handleSubmit({ action: 'select' })}
                >
                  {isSubmitting ? 'Generating...' : 'Generate & Select date'}
                </Button>
              </>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
